import { t } from "@lingui/macro";
import type {
  ResumeLayoutItem as LayoutItem,
  ResumeLayoutPage as LayoutPage,
} from "@reactive-resume/dto";
import mammoth from "mammoth";

import { docxHtmlToPages } from "./docx";
import { ocrCanvasLines } from "./tesseract";
import { imageFileToCanvas, preprocessCanvas } from "./preprocess";
import { pdfGetItems, pdfRenderCanvases } from "./pdf";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  ORCHESTRATEUR D'EXTRACTION — 100 % client, 100 % gratuit (aucune IA)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Chaque source produit des fragments POSITIONNÉS (le contrat du moteur
 *  heuristique serveur, double colonne inclus) :
 *   - PDF natif   : pdfjs getTextContent → positions réelles (pas d'OCR) ;
 *   - PDF scanné  : pdf.js → canvas → Tesseract → bounding boxes de lignes ;
 *   - Photo       : canvas → préprocessing → Tesseract ;
 *   - Word .docx  : mammoth (HTML) → lignes avec titres, gras, puces et tableaux.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export type {
  ResumeLayoutItem as LayoutItem,
  ResumeLayoutLink as LayoutLink,
  ResumeLayoutPage as LayoutPage,
} from "@reactive-resume/dto";

export type ExtractionMethod = "pdf-native" | "ocr" | "docx";

export type ExtractionResult = {
  pages: LayoutPage[];
  method: ExtractionMethod;
};

/** Seuil : en dessous de ce volume de texte, le PDF est considéré comme scanné. */
const NATIVE_TEXT_THRESHOLD = 250;

export type ExtractionProgress = {
  stage: "reading" | "pdf-text" | "ocr" | "structuring";
  progress: number;
  label: string;
};

/** Convertit un texte brut en page une-colonne (lignes synthétiques). */
const textToPages = (text: string, width = 595): LayoutPage[] => {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const items: LayoutItem[] = lines.map((line, index) => ({
    text: line,
    x: 0,
    top: index * 20,
    width,
    height: 12,
  }));
  return [{ width, height: lines.length * 20 + 40, items }];
};

export const extractLayoutFromFile = async (
  file: File,
  onProgress: (progress: ExtractionProgress) => void,
): Promise<ExtractionResult> => {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";

  // ── Word (.docx) — mammoth, navigateur ─────────────────────────────────────
  if (extension === "docx") {
    onProgress({ stage: "reading", progress: 0.2, label: t`Lecture du document Word (.docx)…` });
    const arrayBuffer = await file.arrayBuffer();
    const { value: html } = await mammoth.convertToHtml(
      { arrayBuffer },
      { styleMap: ["p[style-name='Title'] => h1:fresh", "p[style-name='Subtitle'] => h2:fresh"] },
    );
    let pages = docxHtmlToPages(html);
    if (pages[0].items.length < 3) {
      const { value } = await mammoth.extractRawText({ arrayBuffer });
      pages = textToPages(value);
    }
    onProgress({ stage: "reading", progress: 1, label: "Document lu" });
    if (pages[0].items.length < 3) {
      throw new Error(t`Le document Word (.docx) semble vide ou illisible.`);
    }
    return { pages, method: "docx" };
  }

  // ── PDF — texte natif d'abord, OCR si scanné ───────────────────────────────
  if (file.type === "application/pdf" || extension === "pdf") {
    onProgress({ stage: "pdf-text", progress: 0.1, label: t`Analyse du PDF (.pdf)…` });
    const extraction = await pdfGetItems(file, (ratio) =>
      onProgress({
        stage: "pdf-text",
        progress: 0.1 + ratio * 0.5,
        label: t`Extraction du texte du PDF (.pdf)…`,
      }),
    );

    if (extraction.charCount >= NATIVE_TEXT_THRESHOLD) {
      onProgress({ stage: "pdf-text", progress: 1, label: "Texte extrait" });
      return { pages: extraction.pages, method: "pdf-native" };
    }

    // PDF scanné → rendu canvas haute résolution → OCR positionnel
    const canvases = await pdfRenderCanvases(file, 2, (done, total) =>
      onProgress({
        stage: "ocr",
        progress: (done - 1) / total,
        label: `Préparation de la page ${done}/${total}…`,
      }),
    );
    const pages: LayoutPage[] = [];
    for (let index = 0; index < canvases.length; index++) {
      const canvas = preprocessCanvas(canvases[index]);
      const label =
        canvases.length > 1
          ? `Reconnaissance OCR page ${index + 1}/${canvases.length}…`
          : "Reconnaissance OCR…";
      onProgress({ stage: "ocr", progress: index / canvases.length, label });
      const lines = await ocrCanvasLines(canvas, (workerProgress) =>
        onProgress({
          stage: "ocr",
          progress: (index + workerProgress) / canvases.length,
          label,
        }),
      );
      pages.push({ width: canvas.width, height: canvas.height, items: lines });
    }
    const totalLines = pages.reduce((sum, page) => sum + page.items.length, 0);
    if (totalLines < 3) {
      throw new Error(
        "Aucun texte lisible détecté. Essayez une photo plus nette, mieux éclairée et sans reflets.",
      );
    }
    return { pages, method: "ocr" };
  }

  // ── Image (photo de CV) ────────────────────────────────────────────────────
  if (file.type.startsWith("image/")) {
    onProgress({ stage: "ocr", progress: 0.15, label: "Optimisation de l'image…" });
    const canvas = preprocessCanvas(await imageFileToCanvas(file));
    onProgress({ stage: "ocr", progress: 0.35, label: "Reconnaissance OCR…" });
    const lines = await ocrCanvasLines(canvas, (workerProgress) =>
      onProgress({
        stage: "ocr",
        progress: 0.35 + workerProgress * 0.6,
        label: "Reconnaissance OCR…",
      }),
    );
    if (lines.length < 3) {
      throw new Error(
        "Aucun texte lisible détecté. Essayez une photo plus nette, bien éclairée, sans reflets.",
      );
    }
    return {
      pages: [{ width: canvas.width, height: canvas.height, items: lines }],
      method: "ocr",
    };
  }

  throw new Error(
    t`Format non supporté. Utilisez un PDF (.pdf), un Word (.docx) ou une image (JPG (.jpg), PNG (.png)).`,
  );
};
